import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { apiClient } from '~/lib/api';
import { getAccessToken, setTokensFromCallback } from '~/lib/auth';
import { cn } from '~/lib/utils';

export const Route = createFileRoute('/$slug/auth/callback')({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [needsName, setNeedsName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove '#'
    if (!hash) {
      navigate({ to: '/$slug/auth', params: { slug } });
      return;
    }

    const params = new URLSearchParams(hash);
    const token = params.get('token');
    const refreshToken = params.get('refreshToken') || '';
    const userId = params.get('userId');
    const needsNameParam = params.get('needsName') === 'true';

    if (!token || !userId) {
      navigate({ to: '/$slug/auth', params: { slug } });
      return;
    }

    // Store tokens and invalidate stale profile/stats cache
    setTokensFromCallback({ accessToken: token, refreshToken, userId, slug });
    queryClient.invalidateQueries({ queryKey: ['profile'] });
    queryClient.invalidateQueries({ queryKey: ['gameStats'] });

    // Clear fragment from URL
    window.history.replaceState(null, '', window.location.pathname);

    if (needsNameParam) {
      setNeedsName(true);
    } else {
      navigate({ to: '/$slug', params: { slug } });
    }
  }, [navigate, slug]);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || saving) return;

    setSaving(true);
    try {
      const token = getAccessToken();
      await apiClient('/v1/public/profile/display-name', {
        method: 'PATCH',
        body: { displayName: displayName.trim() },
        token,
      });
    } catch {
      // Continue even if name save fails -- user can update later
    }
    navigate({ to: '/$slug', params: { slug } });
  };

  if (needsName) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <h2 className="text-center text-xl font-semibold">
            What should we call you?
          </h2>
          <form onSubmit={handleNameSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm min-h-[44px]',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
              )}
            />
            <button
              type="submit"
              disabled={saving || !displayName.trim()}
              className={cn(
                'w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white min-h-[44px]',
                'hover:bg-blue-700 disabled:opacity-50',
              )}
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/$slug', params: { slug } })}
              className="w-full text-sm text-gray-500 hover:underline min-h-[44px]"
            >
              Skip
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-gray-500">Signing in...</p>
    </div>
  );
}
